import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { settingsApi, Settings, CurrencyInfo, TimezoneInfo, UpdateSettingsRequest } from '../../lib/api';

// Async thunks for settings API calls
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.getSettings();
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch settings');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch settings');
    }
  }
);

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async (data: UpdateSettingsRequest, { rejectWithValue }) => {
    try {
      const response = await settingsApi.updateSettings(data);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update settings');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update settings');
    }
  }
);

export const fetchCurrencies = createAsyncThunk(
  'settings/fetchCurrencies',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.getCurrencies();
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch currencies');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch currencies');
    }
  }
);

export const fetchTimezones = createAsyncThunk(
  'settings/fetchTimezones',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.getTimezones();
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch timezones');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch timezones');
    }
  }
);

// Initial state
interface SettingsState {
  settings: Settings | null;
  currencies: CurrencyInfo[];
  timezones: TimezoneInfo[];
  loading: boolean;
  updating: boolean;
  error: string | null;
  initialized: boolean;
}

const initialState: SettingsState = {
  settings: null,
  currencies: [],
  timezones: [],
  loading: false,
  updating: false,
  error: null,
  initialized: false,
};

// Settings slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSettings: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch settings
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action: PayloadAction<Settings>) => {
        state.loading = false;
        state.settings = action.payload;
        state.initialized = true;
        state.error = null;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update settings
    builder
      .addCase(updateSettings.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action: PayloadAction<Settings>) => {
        state.updating = false;
        state.settings = action.payload;
        state.error = null;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload as string;
      });

    // Fetch currencies
    builder
      .addCase(fetchCurrencies.fulfilled, (state, action: PayloadAction<CurrencyInfo[]>) => {
        state.currencies = action.payload;
      });

    // Fetch timezones
    builder
      .addCase(fetchTimezones.fulfilled, (state, action: PayloadAction<TimezoneInfo[]>) => {
        state.timezones = action.payload;
      });
  },
});

// Export actions
export const { clearError, resetSettings } = settingsSlice.actions;

// Selectors
export const selectSettings = (state: { settings: SettingsState }) => state.settings.settings;
export const selectCurrencies = (state: { settings: SettingsState }) => state.settings.currencies;
export const selectTimezones = (state: { settings: SettingsState }) => state.settings.timezones;
export const selectSettingsLoading = (state: { settings: SettingsState }) => state.settings.loading;
export const selectSettingsUpdating = (state: { settings: SettingsState }) => state.settings.updating;
export const selectSettingsError = (state: { settings: SettingsState }) => state.settings.error;
export const selectSettingsInitialized = (state: { settings: SettingsState }) => state.settings.initialized;

// Helper selectors
export const selectHospitalName = (state: { settings: SettingsState }) => 
  state.settings.settings?.hospitalName || 'HMS Hospital';

export const selectCurrency = (state: { settings: SettingsState }) => 
  state.settings.settings?.currency || 'USD';

export const selectCurrencySymbol = (state: { settings: SettingsState }) => {
  const currency = state.settings.settings?.currency || 'USD';
  const currencyInfo = state.settings.currencies.find(c => c.code === currency);
  return currencyInfo?.symbol || '$';
};

export const selectDateFormat = (state: { settings: SettingsState }) => 
  state.settings.settings?.dateFormat || 'MM/DD/YYYY';

export const selectTimeFormat = (state: { settings: SettingsState }) => 
  state.settings.settings?.timeFormat || '12';

export default settingsSlice.reducer; 